import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  SelectChangeEvent,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Edit,
  Delete,
  Block,
  CheckCircle,
  People,
  AdminPanelSettings,
  Engineering,
  Business,
  Support,
  Person,
  Email,
  Phone,
  LocationOn,
  AccessTime,
  Security,
} from '@mui/icons-material';
import { brandColors } from '../../theme';

interface User {
  id: number;
  name: string;
  email: string;
  role: 'r2b_employee' | 'jordan_field_service' | 'jordan_consultant' | 'branch_manager' | 'inku_employee' | 'customer_employee';
  status: 'active' | 'inactive' | 'pending';
  lastLogin: string;
  createdAt: string;
  avatar?: string;
  phone?: string;
  location?: string;
  permissions: string[];
}

interface Role {
  key: string;
  name: string;
  description: string;
  permissions: string[];
  color: string;
  icon: React.ReactElement;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

const UserManagement: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [roleDetailOpen, setRoleDetailOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    role: '',
    phone: '',
    location: '',
  });

  const roles: Role[] = [
    {
      key: 'r2b_employee',
      name: 'R2B Employee',
      description: 'Vollzugriff auf alle R2B Platform Funktionen',
      permissions: ['create_campaigns', 'manage_content', 'view_analytics', 'manage_users', 'system_admin'],
      color: brandColors.electricTeal,
      icon: <AdminPanelSettings />
    },
    {
      key: 'jordan_field_service',
      name: 'Jordan Field Service & Experts',
      description: 'Zugriff auf Feldservice-spezifische Funktionen',
      permissions: ['view_campaigns', 'create_content', 'view_analytics'],
      color: brandColors.neonLime,
      icon: <Engineering />
    },
    {
      key: 'jordan_consultant',
      name: 'Jordan Consultant',
      description: 'Beratung und Kampagnen-Support',
      permissions: ['view_campaigns', 'create_content', 'view_analytics', 'manage_campaigns'],
      color: brandColors.midnightSteel,
      icon: <Support />
    },
    {
      key: 'branch_manager',
      name: 'Branch Manager',
      description: 'Verwaltung der Filiale und lokaler Kampagnen',
      permissions: ['view_campaigns', 'manage_local_content', 'view_local_analytics'],
      color: '#ff9800',
      icon: <Business />
    },
    {
      key: 'inku_employee',
      name: 'Inku Employee',
      description: 'Inku-spezifische Funktionen und Berechtigungen',
      permissions: ['view_campaigns', 'create_content'],
      color: '#9c27b0',
      icon: <Person />
    },
    {
      key: 'customer_employee',
      name: 'Customer Employee',
      description: 'Kunden-Mitarbeiter mit eingeschränktem Zugriff',
      permissions: ['view_own_campaigns', 'request_content'],
      color: '#607d8b',
      icon: <People />
    },
  ];

  const users: User[] = [
    {
      id: 1,
      name: 'Maria Schmidt',
      email: 'maria.schmidt@r2b.de',
      role: 'r2b_employee',
      status: 'active',
      lastLogin: '2025-09-16 09:30',
      createdAt: '2025-01-15',
      phone: '+49 30 12345678',
      location: 'Berlin',
      permissions: ['create_campaigns', 'manage_content', 'view_analytics', 'manage_users', 'system_admin']
    },
    {
      id: 2,
      name: 'Thomas Müller',
      email: 'thomas.mueller@jordan.de',
      role: 'jordan_field_service',
      status: 'active',
      lastLogin: '2025-09-16 08:15',
      createdAt: '2025-02-20',
      phone: '+49 40 98765432',
      location: 'Hamburg',
      permissions: ['view_campaigns', 'create_content', 'view_analytics']
    },
    {
      id: 3,
      name: 'Anna Weber',
      email: 'anna.weber@jordan.de',
      role: 'jordan_consultant',
      status: 'active',
      lastLogin: '2025-09-15 16:45',
      createdAt: '2025-03-10',
      phone: '+49 89 11223344',
      location: 'München',
      permissions: ['view_campaigns', 'create_content', 'view_analytics', 'manage_campaigns']
    },
    {
      id: 4,
      name: 'Michael Brown',
      email: 'michael.brown@branch.local',
      role: 'branch_manager',
      status: 'active',
      lastLogin: '2025-09-16 07:20',
      createdAt: '2025-04-05',
      phone: '+49 221 55667788',
      location: 'Köln',
      permissions: ['view_campaigns', 'manage_local_content', 'view_local_analytics']
    },
    {
      id: 5,
      name: 'Lisa Johnson',
      email: 'lisa.johnson@inku.de',
      role: 'inku_employee',
      status: 'pending',
      lastLogin: 'Noch nie',
      createdAt: '2025-09-10',
      phone: '+49 711 99887766',
      location: 'Stuttgart',
      permissions: ['view_campaigns', 'create_content']
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'var(--brand)';
      case 'inactive': return 'var(--danger)';
      case 'pending': return 'var(--warning)';
      default: return 'var(--slate-600)';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Aktiv';
      case 'inactive': return 'Inaktiv';
      case 'pending': return 'Ausstehend';
      default: return 'Unbekannt';
    }
  };

  const getRoleDetails = (roleKey: string) => {
    return roles.find(role => role.key === roleKey) || roles[0];
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, userId: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(userId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleNewUserOpen = () => {
    setNewUserOpen(true);
  };

  const handleNewUserClose = () => {
    setNewUserOpen(false);
    setNewUserData({
      name: '',
      email: '',
      role: '',
      phone: '',
      location: '',
    });
  };

  const handleRoleDetailOpen = (role: Role) => {
    setSelectedRole(role);
    setRoleDetailOpen(true);
  };

  const handleRoleDetailClose = () => {
    setRoleDetailOpen(false);
    setSelectedRole(null);
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNewUserData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSelectChange = (field: string) => (event: SelectChangeEvent) => {
    setNewUserData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(part => part.charAt(0)).join('').toUpperCase();
  };

  return (
    <Box>
      {/* Header Section (neutral) */}
      <Box sx={{ mb: 4, p: 3, borderRadius: 'var(--radius)', border: '1px solid var(--line)', backgroundColor: 'var(--card)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h2" sx={{ fontWeight: 600, mb: 1 }}>
              User Management
            </Typography>
            <Typography variant="body1" sx={{ color: 'var(--muted)' }}>
              RBAC-basierte Benutzerverwaltung mit Rollen und Berechtigungen
            </Typography>
          </Box>
          <Button variant="contained" size="large" startIcon={<Add />} onClick={handleNewUserOpen}>
            Neuer Benutzer
          </Button>
        </Box>
      </Box>
      {/* Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card sx={{ border: '1px solid var(--line)', backgroundColor: 'var(--card)', borderRadius: 'var(--radius)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: brandColors.midnightSteel }}>
                    {users.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Gesamt Benutzer
                  </Typography>
                </Box>
                <People sx={{ fontSize: 40, color: 'var(--brand)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card sx={{ border: '1px solid var(--line)', backgroundColor: 'var(--card)', borderRadius: 'var(--radius)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: brandColors.midnightSteel }}>
                    {users.filter(u => u.status === 'active').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Aktive Benutzer
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: 'var(--brand)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card sx={{ border: '1px solid var(--line)', backgroundColor: 'var(--card)', borderRadius: 'var(--radius)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: brandColors.midnightSteel }}>
                    {roles.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Verfügbare Rollen
                  </Typography>
                </Box>
                <AdminPanelSettings sx={{ fontSize: 40, color: 'var(--brand)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card sx={{ border: '1px solid var(--line)', backgroundColor: 'var(--card)', borderRadius: 'var(--radius)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: brandColors.midnightSteel }}>
                    {users.filter(u => u.status === 'pending').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ausstehende
                  </Typography>
                </Box>
                <AccessTime sx={{ fontSize: 40, color: 'var(--brand)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Main Content */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Benutzer" />
            <Tab label="Rollen & Berechtigungen" />
            <Tab label="Aktivitäts-Log" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Benutzer</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Rolle</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Status</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Letzter Login</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Standort</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Aktionen</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => {
                  const roleDetails = getRoleDetails(user.role);
                  return (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: roleDetails.color, width: 40, height: 40 }}>
                            {getInitials(user.name)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600}>{user.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={roleDetails.icon}
                          label={roleDetails.name}
                          size="small"
                          sx={{
                            bgcolor: `${roleDetails.color}20`,
                            color: roleDetails.color,
                            fontWeight: 600
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getStatusText(user.status)} 
                          size="small"
                          sx={{ 
                            bgcolor: `${getStatusColor(user.status)}20`,
                            color: getStatusColor(user.status),
                            fontWeight: 600
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{user.lastLogin}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">{user.location}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={(e) => handleMenuOpen(e, user.id)}>
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: brandColors.midnightSteel }}>
              Rollen Matrix
            </Typography>
            <Grid container spacing={3}>
              {roles.map((role) => (
                <Grid
                  key={role.key}
                  size={{
                    xs: 12,
                    md: 6
                  }}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer', 
                      border: `1px solid ${role.color}30`,
                      '&:hover': { boxShadow: 4 } 
                    }}
                    onClick={() => handleRoleDetailOpen(role)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: role.color, mr: 2 }}>
                          {role.icon}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {role.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {users.filter(u => u.role === role.key).length} Benutzer
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {role.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {role.permissions.length} Berechtigungen
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: brandColors.midnightSteel }}>
              Benutzer Aktivitäten
            </Typography>
            <List>
              <ListItem sx={{ border: '1px solid #f0f0f0', borderRadius: 2, mb: 1 }}>
                <ListItemIcon>
                  <Avatar sx={{ bgcolor: brandColors.electricTeal, width: 32, height: 32 }}>
                    <Person sx={{ fontSize: 16 }} />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary="Maria Schmidt hat eine neue Kampagne erstellt"
                  secondary="Heute, 09:30 • Sommer Handwerker Kampagne"
                />
              </ListItem>
              <ListItem sx={{ border: '1px solid #f0f0f0', borderRadius: 2, mb: 1 }}>
                <ListItemIcon>
                  <Avatar sx={{ bgcolor: brandColors.neonLime, width: 32, height: 32 }}>
                    <Edit sx={{ fontSize: 16 }} />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary="Thomas Müller hat Content bearbeitet"
                  secondary="Heute, 08:15 • Herbst Promotion Banner"
                />
              </ListItem>
              <ListItem sx={{ border: '1px solid #f0f0f0', borderRadius: 2, mb: 1 }}>
                <ListItemIcon>
                  <Avatar sx={{ bgcolor: '#ff9800', width: 32, height: 32 }}>
                    <CheckCircle sx={{ fontSize: 16 }} />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary="Anna Weber hat Inhalte freigegeben"
                  secondary="Gestern, 16:45 • Weihnachts Special Videos"
                />
              </ListItem>
              <ListItem sx={{ border: '1px solid #f0f0f0', borderRadius: 2, mb: 1 }}>
                <ListItemIcon>
                  <Avatar sx={{ bgcolor: brandColors.midnightSteel, width: 32, height: 32 }}>
                    <Add sx={{ fontSize: 16 }} />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary="Neuer Benutzer Lisa Johnson wurde hinzugefügt"
                  secondary="10.09.2025, 14:20 • Inku Employee"
                />
              </ListItem>
            </List>
          </Box>
        </TabPanel>
      </Card>
      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Bearbeiten
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <Security fontSize="small" sx={{ mr: 1 }} />
          Berechtigungen
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <Block fontSize="small" sx={{ mr: 1 }} />
          Deaktivieren
        </MenuItem>
        <MenuItem onClick={handleMenuClose} sx={{ color: '#f44336' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Löschen
        </MenuItem>
      </Menu>
      {/* New User Dialog */}
      <Dialog open={newUserOpen} onClose={handleNewUserClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: brandColors.electricTeal, color: brandColors.clearLight }}>
          Neuen Benutzer hinzufügen
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                label="Vollständiger Name"
                value={newUserData.name}
                onChange={handleInputChange('name')}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                label="E-Mail Adresse"
                type="email"
                value={newUserData.email}
                onChange={handleInputChange('email')}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <FormControl fullWidth>
                <InputLabel>Rolle</InputLabel>
                <Select
                  value={newUserData.role}
                  onChange={handleSelectChange('role')}
                  label="Rolle"
                >
                  {roles.map((role) => (
                    <MenuItem key={role.key} value={role.key}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                label="Telefonnummer"
                value={newUserData.phone}
                onChange={handleInputChange('phone')}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Standort"
                value={newUserData.location}
                onChange={handleInputChange('location')}
              />
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Willkommens-E-Mail senden"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleNewUserClose}>Abbrechen</Button>
          <Button 
            variant="contained" 
            onClick={handleNewUserClose}
            sx={{ 
              bgcolor: brandColors.electricTeal,
              '&:hover': { bgcolor: brandColors.midnightSteel }
            }}
          >
            Benutzer erstellen
          </Button>
        </DialogActions>
      </Dialog>
      {/* Role Details Dialog */}
      <Dialog open={roleDetailOpen} onClose={handleRoleDetailClose} maxWidth="sm" fullWidth>
        {selectedRole && (
          <>
            <DialogTitle sx={{ 
              bgcolor: selectedRole.color, 
              color: brandColors.clearLight,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              {selectedRole.icon}
              {selectedRole.name}
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {selectedRole.description}
              </Typography>
              
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Berechtigungen
              </Typography>
              
              <List>
                {selectedRole.permissions.map((permission, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon>
                      <CheckCircle sx={{ color: selectedRole.color, fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    />
                  </ListItem>
                ))}
              </List>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Zugewiesene Benutzer ({users.filter(u => u.role === selectedRole.key).length})
              </Typography>
              
              {users.filter(u => u.role === selectedRole.key).map((user) => (
                <Box key={user.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Avatar sx={{ bgcolor: selectedRole.color, width: 32, height: 32 }}>
                    {getInitials(user.name)}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{user.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                  </Box>
                </Box>
              ))}
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button onClick={handleRoleDetailClose}>Schließen</Button>
              <Button startIcon={<Edit />}>Rolle bearbeiten</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default UserManagement;
